import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LoggerService } from '../services/logger.service';
import { ConfigService } from '../services/config.service';
import { UsageAnalyticsService } from '../services/usage-analytics.service';
import { ListRequest, Result, ListResponse } from '@shared/models';
import { RetailerSimple, Retailer, RetailerNote, RetailerProgram } from '@shared/entities';
import * as Models from '@shared/models';
import * as Entities from '@shared/entities';

@Injectable({
  providedIn: 'root',
})
export class RetailersApi {
  private readonly http = inject(HttpClient);
  private readonly log = inject(LoggerService);
  private readonly config = inject(ConfigService);
  private readonly usageAnalytics = inject(UsageAnalyticsService);

  // Service URI computed from config signal
  private readonly serviceUri = computed(() => {
    const apiUri = this.config.config()?.apiUri;
    return apiUri ? `${apiUri}/retailers/` : '';
  });

  async loadRetailers(_force: boolean = false): Promise<ListResponse<Retailer>> {
    const result = await lastValueFrom(this.http.get<Result<ListResponse<Retailer>>>(this.serviceUri() + 'list'));
    if (result.successful) {
      return result.data;
    }

    this.log.displayError('Unable to load Retailers', 'RetailersApi->loadRetailers');
    return { records: [] } as ListResponse<Retailer>;
  }

  async loadNotes(_force: boolean = false, retailerId: number): Promise<ListResponse<RetailerNote>> {
    const result = await lastValueFrom(
      this.http.get<Result<ListResponse<RetailerNote>>>(this.serviceUri() + retailerId + '/notes/')
    );
    console.debug('loadNotes', result);
    if (result.successful) {
      return result.data;
    }

    this.log.displayError('Unable to load retailer notes', 'RetailersApi->loadNotes');
    return { records: [] } as ListResponse<RetailerNote>;
  }

  async loadPrograms(_force: boolean = false, retailerId: number): Promise<ListResponse<RetailerProgram>> {
    const result = await lastValueFrom(
      this.http.get<Result<ListResponse<RetailerProgram>>>(this.serviceUri() + retailerId + '/programs/')
    );
    if (result.successful) {
      console.debug('loadPrograms', result);
      return result.data;
    }

    this.log.displayError('Unable to load Retailer programs', 'RetailersApi->loadPrograms');
    return { records: [] } as ListResponse<RetailerProgram>;
  }

  fetchRetailer(id: number): Observable<Retailer> {
    return this.http.get<Retailer>(this.serviceUri() + id);
  }

  fetchAll(): Observable<RetailerSimple[]> {
    return this.http.get<Result<ListResponse<RetailerSimple>>>(this.serviceUri()).pipe(
      map((response) => response.data.records),
      catchError(this.handleError<RetailerSimple[]>('getRetailers', []))
    );
  }

  search(request: ListRequest): Observable<Retailer[]> {
    return this.http.post<ListResponse<Retailer>>(this.serviceUri() + 'Search', request).pipe(
      map((response) => response.records),
      catchError(this.handleError<Retailer[]>('getRetailers', []))
    );
  }

  getRetailerPrograms(request: Models.IRetailerRequestModel): Observable<Entities.RetailerProgram[]> {
    return this.http
      .post<
        Entities.RetailerProgram[]
      >(this.serviceUri() + request.retailerId + '/programs', JSON.stringify(request), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((response: Entities.RetailerProgram[]) => {
          return response;
        }),
        catchError(this.handleError<Entities.RetailerProgram[]>('GetRetailerPrograms', []))
      );
  }

  async create(newRetailer: Retailer): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceUri(), newRetailer));
    console.debug('create-retailer', result);
    if (result.successful) {
      this.log.displaySuccess('Retailer created', 'RetailersApi->create');
      this.usageAnalytics.trackAction('RetailerCreated', {
        props: {
          RetailerId: String(result.data ?? ''),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->create');
    }
    return result;
  }

  async update(retailerId: number, retailer: Retailer): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.put<Result<number>>(this.serviceUri() + retailerId, retailer));
    if (result.successful) {
      this.log.displaySuccess('Retailer updated', 'RetailersApi->update');
      this.usageAnalytics.trackAction('RetailerUpdated', {
        props: {
          RetailerId: String(retailerId),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->update');
    }
    console.debug('update-retailer', result);
    return result;
  }

  async activateProgram(retailerId: number, programId: number): Promise<Result<number>> {
    try {
      const result = await lastValueFrom(
        this.http.put<Result<number>>(this.serviceUri() + retailerId + '/activate/' + programId, null)
      );
      console.debug('activated program', result);
      if (result.successful) {
        this.log.displaySuccess('Program activated for retailer', 'RetailersApi->activateProgram');
        this.usageAnalytics.trackAction('RetailerProgramActivated', {
          props: {
            RetailerId: String(retailerId),
            ProgramId: String(programId),
          },
        });
      } else {
        this.log.displayError(result.message, 'RetailersApi->activateProgram');
      }
      return result;
    } catch (error: unknown) {
      const failureResult = this.createFailureResult<number>(error, 'Unable to activate program.', 0);
      this.log.displayError(failureResult.message, 'RetailersApi->activateProgram');
      return failureResult;
    }
  }

  async activatePhoneNumber(
    retailerId: number,
    retailerProgramId: number,
    callCenterPhoneNumberId: number
  ): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.post<Result<number>>(
        this.serviceUri() + retailerId + '/activate/phone-number/',
        JSON.stringify({ retailerProgramId, callCenterPhoneNumberId }),
        { headers: { 'Content-Type': 'application/json' } }
      )
    );
    if (result.successful) {
      this.log.displaySuccess('Retailer phone number activated successfully', 'RetailersApi->activatePhoneNumber');
    } else {
      this.log.displayError(result.message, 'RetailersApi->activatePhoneNumber');
    }
    return result;
  }

  async deactivate(retailerId: number): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.put<Result<number>>(this.serviceUri() + retailerId + '/deactivate', null)
    );
    if (result.successful) {
      this.log.displaySuccess('Deactivated Retailer', 'RetailersApi->deactivate');
      this.usageAnalytics.trackAction('RetailerDeactivated', {
        props: {
          RetailerId: String(retailerId),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->deactivate');
    }
    console.debug('deactivated retailer', result);
    return result;
  }

  async deactivateProgram(
    retailerId: number,
    programId: number,
    retailerProgramId: number = 0
  ): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.delete<Result<number>>(
        this.serviceUri() + retailerId + '/deactivate/' + programId + '/retailer-program/' + retailerProgramId
      )
    );
    if (result.successful) {
      this.log.displaySuccess('Program deactivated for retailer', 'RetailersApi->deactivateProgram');
      this.usageAnalytics.trackAction('RetailerProgramDeactivated', {
        props: {
          RetailerId: String(retailerId),
          ProgramId: String(programId),
          RetailerProgramId: String(retailerProgramId),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->deactivateProgram');
    }
    console.debug('deactivated program', result);
    return result;
  }

  async deactivatePhoneNumber(retailerId: number, retailerPhoneNumberId: number): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.delete<Result<number>>(
        this.serviceUri() + retailerId + '/deactivate/phone-number/' + retailerPhoneNumberId
      )
    );
    if (result.successful) {
      this.log.displaySuccess(
        result.message || 'Phone number deactivated for retailer',
        'RetailersApi->deactivatePhoneNumber'
      );
    } else {
      this.log.displayError(result.message, 'RetailersApi->deactivatePhoneNumber');
    }
    console.debug('deactivated phone number', result);
    return result;
  }

  async createRetailerNote(retailerId: number, retailerNote: Entities.RetailerNote): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.post<Result<number>>(this.serviceUri() + retailerId + '/notes/', JSON.stringify(retailerNote), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
    if (result.successful) {
      this.log.displaySuccess('Retailer note created', 'RetailersApi->createRetailerNote');
      this.usageAnalytics.trackAction('RetailerNoteCreated', {
        props: {
          RetailerId: String(retailerId),
          RetailerNoteId: String(result.data ?? ''),
          NoteTypeId: String(retailerNote.noteTypeId ?? ''),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->createRetailerNote');
    }
    console.debug('createRetailerNote', result);
    return result;
  }

  async updateRetailerNote(retailerId: number, retailerNote: Entities.RetailerNote): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.put<Result<number>>(
        this.serviceUri() + retailerId + '/notes/' + retailerNote.retailerNoteId,
        JSON.stringify(retailerNote),
        { headers: { 'Content-Type': 'application/json' } }
      )
    );
    if (result.successful) {
      this.log.displaySuccess('Retailer note updated', 'RetailersApi->updateRetailerNote');
      this.usageAnalytics.trackAction('RetailerNoteUpdated', {
        props: {
          RetailerId: String(retailerId),
          RetailerNoteId: String(retailerNote.retailerNoteId ?? ''),
          NoteTypeId: String(retailerNote.noteTypeId ?? ''),
        },
      });
    } else {
      this.log.displayError(result.message, 'RetailersApi->updateRetailerNote');
    }
    console.debug('updateRetailerNote', result);
    return result;
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      this.log.logError(error);
      return of(result as T);
    };
  }

  private createFailureResult<T>(error: unknown, fallbackMessage: string, fallbackData: T): Result<T> {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Partial<Result<T>> | null | undefined;
      const message =
        typeof body?.message === 'string' && body.message.length > 0 ? body.message : error.message || fallbackMessage;
      const data = (body?.data as T | undefined) ?? fallbackData;
      return new Result<T>(false, message, data, []);
    }

    if (error instanceof Error) {
      return new Result<T>(false, error.message || fallbackMessage, fallbackData, []);
    }

    return new Result<T>(false, fallbackMessage, fallbackData, []);
  }
}
